import { Vue, Options } from 'vue-class-component';
import {
  EntrancePlantDetailDTO,
  EntrancePlantHeadCreateDTO,
  EntrancePlantStructureCreateDTO,
  PePlantConfigDTO,
  PlantIconEnum,
  PlantTagDTO,
  PlantType,
} from '@/../client-generator/generated-clients/plants';
import plantsClient from '@/core/services/plants';
import {
  getEpPlantIconImages,
  getEpPlantIconImageUrls,
} from '@/core/utils/utils';
import {
  Country,
  PlantEntityType,
} from '@/../client-generator/generated-clients/configurations';
import { NamingService } from '@/core/services/naming.service';
import configurationsClient from '@/core/services/configurations-service';
import { mapGetters } from 'vuex';
import { PropType } from 'vue';

const kebabCase = require('lodash.kebabcase');

@Options({
  name: 'DialogEntrancePlantCreateStep1',
  props: {
    plant: {
      required: true,
      type: Object as PropType<EntrancePlantDetailDTO>,
    },
    plantId: {
      type: Number,
      required: false,
    },
    structureCreate: {
      required: false,
      type: Object as () => EntrancePlantStructureCreateDTO,
    },
    preSelectedTags: {
      type: Object as PropType<PlantTagDTO[]>,
    },
    isEdit: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    'plant.name': function () {
      this.verifyValidityOfName();
    },
    'plant.address': function () {
      this.checkNextButtonState();
    },
    'plant.timeZone': function () {
      this.updateCountryFromTimeZone();
      this.checkNextButtonState();
    }
  },
  emits: ['is-loading', 'onBack', 'onNext', 'plant-edited'],
  computed: {
    ...mapGetters('UI', ['plantEditImageCustomEnabled']),
  },
})
export default class DialogEntrancePlantCreateStep1 extends Vue {
  plant!: EntrancePlantDetailDTO;
  isEdit!: boolean;
  initialName!: string;
  preSelectedTags!: PlantTagDTO[] | undefined;
  plantEditImageCustomEnabled!: boolean;
  structureCreate!: EntrancePlantStructureCreateDTO;

  plantId: number | null = null;

  tempTags: PlantTagDTO[] = [];
  countries: Country[] = [];
  newTagName = '';
  nextEnabled = false;

  // Naming validation props
  isValidName = true;
  invalidNameReason: string = '';

  get title() {
    return this.$t(
      this.plantId ? 'AppResourceEP.EditEntrancePlantDetails' : 'AppResourceEP.CreateEntrancePlant'
    ).toString();
  }

  get isCreateTagDisabled() {
    return !this.newTagName.trim();
  }

  get images() {
    const images = [
      ...getEpPlantIconImages().map((image) => ({
        imageURLs: getEpPlantIconImageUrls(image),
        value: image.value,
      })),
    ];
    if (this.plantEditImageCustomEnabled)
      images.push({
        imageURLs: [
          this.plant.customIconDataUrl ||
            (this.plant as any).customIconImageUrl ||
            require(`@/assets/images/plants/add-your-image-here.jpg`),
        ],
        value: PlantIconEnum.ICON_CUSTOM,
      });
    return images;
  }

  async created() {
    if (this.plant.tags && this.plant.tags.length > 0) {
      this.tempTags = this.plant.tags;
    }

    if (
      !this.tempTags ||
      this.tempTags.length === 0 ||
      this.countries.length === 0
    ) {
      this.$emit('is-loading', true);
      
      try {
        await Promise.all([this.fetchPlantTags(), this.fetchCountries()]);
      } finally {
        this.$emit('is-loading', false);
      }
    }

    if (!this.plantId) {
      if (!this.plant.timeZone) {
        this.plant.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      }
      if (this.plant.timeZone) {
        this.updateCountryFromTimeZone();
      }
    }
  }

  async beforeMount() {
    this.verifyValidityOfName();
    this.checkNextButtonState();

    NamingService.flushCache();
    await NamingService.prefetchFlatEntities(
      0, // this must be 0, as from API specification
      PlantEntityType.PLANT
    );
  }

  updated() {
    if (this.plantId && this.plant?.name) this.initialName = this.plant.name;
  }

  fileRead(imageBinary: string | undefined) {
    if (!imageBinary) {
      this.plant.icon = undefined;
    }
    this.plant.customIconDataUrl = imageBinary;
  }

  async fetchPlantTags() {
    try {
      this.tempTags = await plantsClient.tagsList();
    } catch (error) {
      console.log(error);
    }
  }

  async fetchCountries() {
    try {
      this.countries = await configurationsClient.countriesAutocomplete(PlantType.ENTRANCE_PLANT);
    } catch (error) {
      console.log(error);
    }
  }

  private updateCountryFromTimeZone() {
    this.plant.country = this.countries.find(
      (c: Country) => c.timeZoneId === this.plant.timeZone
    )?.code;
  }

  addTag() {
    this.newTagName = this.newTagName.trim();

    if (!this.newTagName) return;

    const tagKey = kebabCase(this.newTagName);

    if (!this.plant.tags) {
      console.log('plant.tags undefined');
      return;
    }
    if (this.plant.tags.some((t) => t.key == tagKey)) {
      return;
    }
    const newTag = new PlantTagDTO({
      key: tagKey,
      value: this.newTagName,
    });
    this.tempTags.push(newTag);
    this.plant.tags.push(newTag);

    this.newTagName = '';
  }

  toggleTagSelection(tag: PlantTagDTO) {
    if (!this.plant.tags) {
      console.log('plant.tags undefined');
      return;
    }

    const groupIndex = this.plant.tags.findIndex((g) => g.key === tag.key);

    if (groupIndex >= 0) {
      this.plant.tags.splice(groupIndex, 1);
    } else {
      this.plant.tags.push(tag);
    }
  }

  isTagSelected(tag: PlantTagDTO) {
    return this.plant.tags?.some((x) => x.key === tag.key);
  }

  private async verifyValidityOfName() {
    const regex = new RegExp("^[\\w\\sÀ-ÖØ-öø-ÿ:.,_-]*$");
    if (!regex.test(this.plant.name!)) {
      this.isValidName = false;
      this.invalidNameReason = this.$t('AppResource.characternotallowed');
      this.nextEnabled = false;
      return;
    }
    const usedNames = NamingService.initialEntities;

    if (this.plantId) {
      const idx = usedNames.findIndex((e) => e.id === this.plantId!.toString());
      if (idx > -1) usedNames.splice(idx, 1);
    }
    const validName = usedNames.every((e) => e.currentName?.toLowerCase().trim() !== this.plant.name?.toLowerCase().trim());
    if (this.plant.name && this.initialName !== this.plant.name && !validName) {
      const suggestedName = await NamingService.suggestNextValidEntityName({
        plantId: this.plantId!,
        eType: PlantEntityType.PLANT,
        metadata: { plantSourceName: this.plant.name },
      });
      this.isValidName = validName;
      this.invalidNameReason = this.$t(
        `uniqueNaming.validationErrorWithSuggestion`,
        { suggestedName }
      );
      this.nextEnabled = false;
    } else {
      this.isValidName = true;
      this.invalidNameReason = '';
      this.nextEnabled = true;
    }
    this.checkNextButtonState();
  }

  private checkNextButtonState() {
    this.nextEnabled = this.plant.name && this.isValidName && this.plant.address && this.plant.timeZone ? true : false;
  }

  getDataToSave() {
    const structure = new EntrancePlantStructureCreateDTO({
      wizardStep: 2,
      headCreate: new EntrancePlantHeadCreateDTO({
        address: this.plant.address,
        country: this.plant.country,
        timeZone: this.plant.timeZone,
        customIconDataUrl: this.plant.customIconDataUrl,
        customIconImageUrl: this.plant.customIconImageUrl,
        desktopId: this.plant.desktopId,
        icon : this.plant.icon,
        name: this.plant.name,
        plantType: this.plant.plantType,
        tags: this.plant.tags
      }),
    });
    this.structureCreate.headCreate = structure.headCreate;
    return structure;
  }

  get nextButtonLabel() {
      return this.plantId ? this.$t('save') : this.$t('saveAndContinue');
  }

  handleBackButton(){
    if (this.plantId) return;
    this.$emit("onBack")
  }

  async handleNextButton(){
    if (this.plantId) {
      await this.updatePlantStructure(this.getDataToSave());
      this.structureCreate.previousWizardPage = 'DialogEntrancePlantCreateStep1';
      if (this.isEdit) {
        this.$emit('plant-edited');
      } else {
        this.$emit("onNext", "DialogEntrancePlantCreateStep2")
      }
    } else {
      const peId = await this.createPlant(this.getDataToSave());
      if (!peId) return;
      this.$emit("onNext", "DialogEntrancePlantCreateStep2", peId)
    }
  }

  async createPlant(structure: EntrancePlantStructureCreateDTO) {
    let createdPlantId = null;
    this.$emit('is-loading', true);

    try {
      createdPlantId = await plantsClient.entranceplantPOST(structure);
      this.structureCreate.headCreate = structure.headCreate;
    } catch (error) {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 6000,
      });
    } finally {
      this.$emit('is-loading', false);
    }

    return createdPlantId;
  }

  async updatePlantStructure(structure: EntrancePlantStructureCreateDTO) {
    if (!this.plantId) return;
    this.$emit('is-loading', true);

    try {
      const result = await plantsClient.structure(this.plant.desktopId!, structure);
      this.structureCreate.headCreate = result.headCreate;
    } catch (error) {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 6000,
      });
    } finally {
      this.$emit('is-loading', false);
    }
  }
}
